import { createTheme } from "@mui/material/styles";
import { CSSProperties } from "@mui/material/styles/createMixins";
import { PaletteOptions, TypeText } from "@mui/material/styles/createPalette";
import { TypographyOptions } from "@mui/material/styles/createTypography";

const titleFontFamily = '"FormularMedium", "Helvetica", "Arial", sans-serif';
const fontFamily = '"Inter", "Helvetica", "Arial", sans-serif';

const htmlFontSize = 16;
const fontWeightMedium = 500;
const fontWeightRegular = 400;

const typography: TypographyOptions = {
  fontFamily,
  htmlFontSize: htmlFontSize,
  fontSize: 16,
  fontWeightLight: 300,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightBold: 700,
  h1: {
    paddingTop: 12,
    fontFamily: titleFontFamily,
    fontSize: `${96 / htmlFontSize}rem`,
    "@media (max-width:600px)": {
      fontSize: `${48 / htmlFontSize}rem`,
    },
    "@media (pointer:coarse)": {
      fontSize: `${48 / htmlFontSize}rem`,
    },
    lineHeight: 1.1,
    letterSpacing: `${-2 / htmlFontSize}rem`,
    fontWeight: fontWeightMedium,
  },
  h2: {
    paddingTop: 12,
    fontFamily: titleFontFamily,
    fontSize: `${64 / htmlFontSize}rem`,
    "@media (max-width:600px)": {
      fontSize: `${36 / htmlFontSize}rem`,
    },
    "@media (pointer:coarse)": {
      fontSize: `${36 / htmlFontSize}rem`,
    },
    lineHeight: 1.2,
    letterSpacing: `${-1.5 / htmlFontSize}rem`,
    fontWeight: fontWeightMedium,
  },
  h3: {
    paddingTop: 12,
    fontFamily: titleFontFamily,
    fontSize: `${36 / htmlFontSize}rem`,
    "@media (max-width:600px)": {
      fontSize: `${24 / htmlFontSize}rem`,
    },
    "@media (pointer:coarse)": {
      fontSize: `${24 / htmlFontSize}rem`,
    },
    lineHeight: 1.2,
    letterSpacing: `${-1 / htmlFontSize}rem`,
    fontWeight: fontWeightMedium,
  },
  body1: {
    fontFamily,
    fontWeight: fontWeightRegular,
    fontSize: `${20 / htmlFontSize}rem`,
    "@media (max-width:600px)": {
      fontSize: `${18 / htmlFontSize}rem`,
    },
    lineHeight: 1.5,
  },
  body2: {
    fontFamily,
    fontWeight: fontWeightRegular,
    fontSize: `${18 / htmlFontSize}rem`,
    "@media (max-width:600px)": {
      fontSize: `${16 / htmlFontSize}rem`,
    },
    "@media (pointer:coarse)": {
      fontSize: `${16 / htmlFontSize}rem`,
    },
    lineHeight: 1.5,
  },
  statement: {
    fontFamily,
    fontWeight: fontWeightRegular,
    fontSize: `${36 / htmlFontSize}rem`,
    "@media (max-width:600px)": {
      fontSize: `${24 / htmlFontSize}rem`,
    },
    "@media (pointer:coarse)": {
      fontSize: `${24 / htmlFontSize}rem`,
    },
    lineHeight: 1.4,
  },
  subtitle: {
    fontFamily: titleFontFamily,
    fontWeight: fontWeightMedium,
    fontSize: `${22 / htmlFontSize}rem`,
    "@media (max-width:600px)": {
      fontSize: `${20 / htmlFontSize}rem`,
    },
    "@media (pointer:coarse)": {
      fontSize: `${20 / htmlFontSize}rem`,
    },
    lineHeight: 1.2,
    letterSpacing: `${-0.5 / htmlFontSize}rem`,
  },
};

const basePalette: PaletteOptions = {
  primary: {
    light: "#F3F3F3",
    main: "#F3F3F3",
    dark: "#F3F3F3",
  },
  secondary: {
    light: "#302D3A",
    main: "#302D3A",
    dark: "#302D3A",
  },
  error: {
    light: "#FDEFED",
    main: "#FF7155",
  },
  success: {
    light: "#E9FFDB",
    main: "#C0FF99",
    dark: "#6CD22D",
  },
};

const textPalette: TypeText = {
  primary: "#302D3A",
  secondary: "#FFF",
  disabled: "#D6D6D6",
};

const lightPalette = createTheme({
  palette: {
    mode: "light",
    ...basePalette,
    background: {
      default: "#FFFFFF",
      paper: "#F3F3F3",
    },
    text: textPalette,
  },
}).palette;

const theme = (palette: PaletteOptions) =>
  createTheme({
    palette,
    typography,
    breakpoints: {
      keys: ["xs", "sm", "md", "lg", "xl"],
      values: {
        xs: 0,
        sm: 600,
        md: 960,
        lg: 1280,
        xl: 1920,
      },
    },
    components: {
      MuiTypography: {
        defaultProps: {
          variantMapping: {
            subtitle: "div",
            statement: "div",
          },
        },
        styleOverrides: {
          root: ({ theme: defaultTheme }) => ({
            marginBottom: "16px",
          }),
        },
      },
      MuiLink: {
        defaultProps: {
          underline: "hover",
          color: "inherit",
        },
      },
    },
  });

const oldThemes = {
  oldLight: theme(lightPalette),
};

export default oldThemes;